import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const langLabels: Record<string, string> = {
  pt: 'PT',
  en: 'EN',
  de: 'DE',
};

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = (i18n.language || 'pt').split('-')[0] === 'en' || (i18n.language || 'pt').split('-')[0] === 'de'
    ? (i18n.language || 'pt').split('-')[0]
    : 'pt';

  const isHome =
    location.pathname === '/' ||
    location.pathname === '/pt' ||
    location.pathname === '/en' ||
    location.pathname === '/de';

  // Solid background when scrolled, on non-home pages, or when mobile menu is open
  const solid = scrolled || !isHome || mobileOpen;

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  function changeLang(lng: string) {
    i18n.changeLanguage(lng);
    setLangOpen(false);
  }

  const textColor = solid ? 'text-foreground-950' : 'text-white';
  const linkColor = solid ? 'text-foreground-700' : 'text-white/90';
  const navBg = solid ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent';

  const shoppingGuidePath = `/${currentLang}/guide/cssbuy-buying-guide`;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="px-4 md:px-6 py-3">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <span className={`font-heading font-bold text-lg ${textColor} transition-colors`}>
              CSSBuy Spreadsheet
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-6">
            <Link to="/" className={`text-sm font-medium cursor-pointer whitespace-nowrap ${linkColor} hover:text-foreground-900 transition-colors`}>
              {t('nav.home')}
            </Link>

            <div ref={catRef} className="relative">
              <button
                onClick={() => setCatOpen(!catOpen)}
                className={`flex items-center gap-1 text-sm font-medium cursor-pointer whitespace-nowrap ${linkColor} hover:text-foreground-900 transition-colors`}
              >
                {t('nav.categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-background-200/70 py-2 max-h-[420px] overflow-y-auto">
                  <div className="px-3 py-1.5 text-xs font-semibold text-foreground-500 uppercase tracking-wide">
                    {t('nav.allCategories')}
                  </div>
                  {categories.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                    >
                      <img src={cat.image} alt={cat.label} className="w-8 h-8 rounded-md object-cover" />
                      <span>{cat.label}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to={shoppingGuidePath} className={`text-sm font-medium cursor-pointer whitespace-nowrap ${linkColor} hover:text-foreground-900 transition-colors`}>
              {t('nav.shoppingGuide')}
            </Link>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className={`text-sm font-medium cursor-pointer whitespace-nowrap ${linkColor} hover:text-foreground-900 transition-colors`}>
              {t('nav.guides')}
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className={`text-sm font-medium cursor-pointer whitespace-nowrap ${linkColor} hover:text-foreground-900 transition-colors`}>
              {t('nav.review')}
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className={`text-sm font-medium cursor-pointer whitespace-nowrap ${linkColor} hover:text-foreground-900 transition-colors`}>
              {t('nav.shipping')}
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className={`text-sm font-medium cursor-pointer whitespace-nowrap ${linkColor} hover:text-foreground-900 transition-colors`}>
              {t('nav.safety')}
            </a>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 md:gap-3">
            <div ref={langRef} className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1 text-sm font-medium cursor-pointer whitespace-nowrap px-2 py-1 rounded-md transition-colors ${solid ? 'text-foreground-800 bg-background-100' : 'text-white/90 bg-white/10'}`}
              >
                {langLabels[currentLang] || 'PT'}
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-20 bg-white rounded-lg shadow-lg border border-background-200/70 py-1 z-50">
                  {['pt', 'en', 'de'].map((lng) => (
                    <button
                      key={lng}
                      onClick={() => changeLang(lng)}
                      className={`block w-full text-left px-3 py-2 text-sm cursor-pointer ${currentLang === lng ? 'text-primary-600 font-semibold bg-primary-50' : 'text-foreground-700 hover:bg-background-100'}`}
                    >
                      {langLabels[lng]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex bg-primary-500 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.explore')}
            </a>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden flex items-center justify-center w-9 h-9 rounded-md cursor-pointer transition-colors ${solid ? 'text-foreground-900 bg-background-100' : 'text-white bg-white/10'}`}
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden mt-3 bg-white rounded-xl border border-background-200/70 shadow-lg overflow-hidden max-h-[80vh] overflow-y-auto">
            <div className="p-4 space-y-1">
              <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer">
                <i className="ri-home-line text-primary-600" />
                {t('nav.home')}
              </Link>
              <Link to={shoppingGuidePath} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer">
                <i className="ri-shopping-bag-line text-primary-600" />
                {t('nav.shoppingGuide')}
              </Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer">
                <i className="ri-book-open-line text-primary-600" />
                {t('nav.guides')}
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer">
                <i className="ri-star-line text-primary-600" />
                {t('nav.review')}
              </a>
            </div>

            <div className="border-t border-background-200/70 p-4">
              <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2 px-3">
                {t('nav.allCategories')}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.key}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 p-2 rounded-lg text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
                  >
                    <img src={cat.image} alt={cat.label} className="w-9 h-9 rounded-md object-cover flex-shrink-0" />
                    <span className="text-xs leading-tight">{cat.label}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/70 p-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full bg-primary-500 text-white text-sm font-medium px-4 py-3 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('nav.explore')}
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}